import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Ship, GraduationCap, Cpu, ChevronDown, Quote } from "lucide-react";
import { Reveal, MaskedLines, Counter, Marquee } from "@/components/motion";
import CTASection from "@/components/CTASection";
import usePageMeta from "@/hooks/usePageMeta";
import useTestimonials from "@/hooks/useTestimonials";
import { BRAND, STATS, PILLARS, WHY_POINTS, IMAGES, MARQUEE_ITEMS } from "@/data/content";

const ICONS = { Ship, GraduationCap, Cpu };

export default function Home() {
  usePageMeta(
    `${BRAND.name} — Global Trade Expert & AI Strategist`,
    "40 years of Export-Import expertise, now powered by AI. Trade consulting, MSME training, AI strategy and web development."
  );

  const heroRef = useRef(null);
  const testimonials = useTestimonials();
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="Container port at night" className="h-[125%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#051320]/65 via-[#051320]/35 to-[#051320]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,rgba(5,19,32,0.55)_100%)]" />
        </motion.div>

        <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-6xl px-6 pb-28 pt-36 text-center">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="kicker mb-10 flex items-center justify-center gap-4 text-gold"
          >
            <span className="h-px w-12 bg-gold/60" />
            Trade · Teach · Tech
            <span className="h-px w-12 bg-gold/60" />
          </motion.p>

          <h1 className="font-serif text-[2.6rem] leading-[1.06] tracking-tight text-white sm:text-6xl lg:text-[5.25rem]">
            <MaskedLines
              lines={["40 Years of Global", "Trade Expertise."]}
              delay={0.3}
            />
            <MaskedLines
              lines={["Now Powered by AI."]}
              delay={0.62}
              lineClassName="italic bg-gradient-to-r from-gold via-gold-light to-teal bg-clip-text text-transparent"
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
            className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg"
          >
            {BRAND.positioning}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Link to="/contact" data-testid="hero-cta-button" className="btn-primary">
              Book a Free Consultation <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
            </Link>
            <button
              data-testid="hero-explore-button"
              onClick={() => document.getElementById("pillars")?.scrollIntoView({ behavior: "smooth" })}
              className="btn-secondary"
            >
              Explore My Services
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}>
            <ChevronDown className="h-6 w-6 text-gold/70" strokeWidth={1.5} />
          </motion.div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-[#1E3A5F] bg-[#0A2540]/30 py-7">
        <Marquee items={MARQUEE_ITEMS} />
      </div>

      {/* TRUST BAR */}
      <section className="py-20 md:py-24" data-testid="trust-bar">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-x-6 gap-y-12 px-6 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className="text-center">
              <Counter
                value={s.value}
                suffix={s.suffix}
                className="font-serif text-5xl text-gold md:text-6xl"
              />
              <p className="mt-3 text-xs uppercase tracking-[0.25em] text-slate-400">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section id="pillars" className="py-24 md:py-32" data-testid="pillars-section">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-16 max-w-2xl">
            <Reveal>
              <p className="kicker mb-5 text-gold">What I Do</p>
              <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-white md:text-5xl">
                Trade, Teach & Tech. <span className="italic text-gold">One Partner.</span>
              </h2>
              <p className="mt-6 text-base leading-relaxed text-slate-400 md:text-lg">
                Whether you want to trade across borders, learn how it's done, or bring your business
                into the digital age — you get four decades of experience behind every engagement.
              </p>
            </Reveal>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => {
              const Icon = ICONS[p.icon];
              const teal = p.accent === "teal";
              return (
                <Reveal key={p.slug} delay={i * 0.12} className="h-full">
                  <Link
                    to={p.to}
                    data-testid={`pillar-card-${p.slug}`}
                    className={`group relative flex h-full flex-col overflow-hidden rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-10 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-2 ${
                      teal
                        ? "hover:border-teal/60 hover:shadow-[0_20px_40px_-15px_rgba(0,180,216,0.3)]"
                        : "hover:border-gold/60 hover:shadow-[0_20px_40px_-15px_rgba(201,162,39,0.3)]"
                    }`}
                  >
                    <span className="pointer-events-none absolute -right-2 -top-4 font-serif text-[7rem] leading-none text-white/[0.04]">
                      {p.number}
                    </span>
                    <div
                      className={`flex h-14 w-14 items-center justify-center rounded-full border ${
                        teal ? "border-teal/40 text-teal" : "border-gold/40 text-gold"
                      }`}
                    >
                      <Icon className="h-6 w-6" strokeWidth={1.5} />
                    </div>
                    <h3 className="mt-8 font-serif text-2xl leading-snug text-white">{p.title}</h3>
                    <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">{p.desc}</p>
                    <span
                      className={`mt-8 inline-flex items-center gap-2 text-sm font-medium tracking-wide ${
                        teal ? "text-teal" : "text-gold"
                      }`}
                    >
                      {p.cta}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.5} />
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* VIDEO */}
      <section className="py-24 md:py-32" data-testid="exim-video-section">
        <div className="mx-auto max-w-5xl px-6">
          <Reveal className="mb-12 text-center">
            <p className="kicker mb-5 text-gold">In Motion</p>
            <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-white md:text-5xl">
              See the Business <span className="italic text-gold">in Action.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative mx-auto max-w-2xl">
              <div className="absolute -inset-3 rounded-3xl border border-gold/30" />
              <div className="relative overflow-hidden rounded-2xl border border-[#1E3A5F] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
                <video
                  data-testid="exim-video"
                  className="aspect-square w-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                >
                  <source src={IMAGES.videoWebm} type="video/webm" />
                  <source src={IMAGES.video} type="video/mp4" />
                </video>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY WORK WITH ME — numbered manifesto chapters */}
      <section className="py-24 md:py-32" data-testid="why-section">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-14 max-w-2xl">
            <Reveal>
              <p className="kicker mb-5 text-gold">Why Work With Me</p>
              <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-white md:text-5xl">
                Experience You Can <span className="italic text-gold">Actually Use.</span>
              </h2>
            </Reveal>
          </div>
          <div className="border-t border-[#1E3A5F]">
            {WHY_POINTS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.06}>
                <div className="grid items-baseline gap-3 border-b border-[#1E3A5F] py-10 md:grid-cols-12 md:gap-6">
                  <span className="font-serif text-4xl text-gold/35 md:col-span-2">0{i + 1}</span>
                  <h3 className="font-serif text-2xl text-white md:col-span-4 md:text-3xl">{w.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-400 md:col-span-6 md:text-base">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL PREVIEW */}
      <section className="py-24 md:py-32" data-testid="testimonial-preview">
        <div className="mx-auto max-w-6xl px-6">
          <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <Reveal>
              <p className="kicker mb-5 text-gold">Kind Words</p>
              <h2 className="font-serif text-4xl leading-[1.1] tracking-tight text-white md:text-5xl">
                Trusted by Businesses <span className="italic text-gold">Like Yours.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <Link
                to="/testimonials"
                data-testid="testimonials-view-all-link"
                className="group inline-flex items-center gap-2 text-sm font-medium text-gold"
              >
                View All
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {testimonials.slice(0, 2).map((t, i) => (
              <Reveal key={i} delay={i * 0.12} className="h-full">
                <figure className="flex h-full flex-col rounded-2xl border border-[#1E3A5F] bg-[#0A2540] p-10">
                  <Quote className="h-8 w-8 text-gold/50" strokeWidth={1.5} />
                  <blockquote className="mt-6 flex-1 font-serif text-xl italic leading-relaxed text-slate-200">
                    "{t.quote}"
                  </blockquote>
                  <figcaption className="mt-8">
                    <p className="text-sm font-semibold text-white">{t.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-slate-500">{t.role}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
